import { Slot, Spine } from '@esotericsoftware/spine-pixi'
import { numberAnimation, registerSpaceBarEvent, sleep, soundManager, useTurboStore } from '@slot-nx/core'
import { BitmapText, Container as PixiContainer } from 'pixi.js'
import { registerNormalSpaceBarEvent } from '../spin/SpinUtil'
import { GameSoundType } from '../sound/GameSoundType'

interface IAnimation {
  task: () => void
  duration: number
}

export const bigWindOddsThreshold = 20
function isMegaWin(odds: number): boolean {
  return odds >= 50 && odds < 100
}
function isSuperWin(odds: number): boolean {
  return odds >= 100
}
export class WinningManager {
  public winningPanelSpine: Spine
  public winningPanel: PixiContainer

  private createWinPanelNumberText() {
    const textComponent = new BitmapText('', { fontName: 'fontC', fontSize: 28 })
    textComponent.anchor.set(0.5, 1.5)
    return textComponent
  }

  private getWinningPanelAnimations(odds: number, spine: Spine) {
    const isTurbo = useTurboStore.getState().isTurbo

    const makeAnim = (inName: string, loopName: string) => {
      return {
        duration: 3000,
        task: () => {
          const track = spine.state.setAnimation(0, inName, false)
          setTimeout(() => {
            spine.state.setAnimation(0, loopName, true)
          }, (track.animation?.duration ?? 0) * 1000)
        },
      }
    }

    // === SUPER WIN ===
    if (isSuperWin(odds)) {
      const normalSequence = [makeAnim('big_in', 'big_loop'), makeAnim('mega_in', 'mega_loop'), makeAnim('super_in', 'super_loop')]
      const turboSequence = [makeAnim('super_in_turbo', 'super_loop')]

      return {
        animation: isTurbo ? turboSequence : normalSequence,
        animationOutName: 'super_out',
      }
    }

    // === MEGA WIN ===
    if (isMegaWin(odds)) {
      const normalSequence = [makeAnim('big_in', 'big_loop'), makeAnim('mega_in', 'mega_loop')]
      const turboSequence = [makeAnim('mega_in_turbo', 'mega_loop')]

      return {
        animation: isTurbo ? turboSequence : normalSequence,
        animationOutName: 'mega_out',
      }
    }
    // === BIG WIN ===
    return {
      animation: [makeAnim('big_in', 'big_loop')],
      animationOutName: 'big_out',
    }
  }

  private playWinningPanelAnimations(animations: IAnimation[]): [Promise<void>, () => void] {
    let isSkip = false
    let anim
    let stopTime = new Date().getTime()

    const task = new Promise<void>((resolve) => {
      const takeNextAnim = () => {
        window.requestAnimationFrame(async () => {
          if (isSkip) {
            anim = animations.pop()
            if (anim) {
              anim.task()
            }
            resolve()
            return
          }
          const now = new Date().getTime()
          if (now >= stopTime) {
            anim = animations.shift()
            if (!anim) {
              resolve()
              return
            }
            anim.task()
            stopTime = now + anim.duration
          }
          takeNextAnim()
        })
      }
      takeNextAnim()
    })

    const skip = () => {
      isSkip = true
    }

    return [task, skip]
  }

  private createWinningPanelSpine() {
    this.winningPanelSpine = Spine.from('winningPanel.skel', 'winningPanel.atlas')
    this.winningPanelSpine.y = 0
    this.winningPanel.addChild(this.winningPanelSpine)
  }

  public async showWinningPanel(odds: number, payout: number) {
    let handleSkip = false
    const isTurbo = useTurboStore.getState().isTurbo
    soundManager.stopBGM()
    const winAmountNode = this.createWinPanelNumberText()
    this.createWinningPanelSpine()
    const spineSlot = this.winningPanelSpine.skeleton.findSlot('bind_score') as Slot
    const delayForSlotSetting = 50

    setTimeout(() => {
      this.winningPanelSpine.addSlotObject(spineSlot, winAmountNode)
    }, delayForSlotSetting)
    this.winningPanel.visible = true

    const panelAnimations = this.getWinningPanelAnimations(odds, this.winningPanelSpine)
    const animationTime = panelAnimations.animation.reduce((acc, cur) => acc + cur.duration, 0) / 1000
    const lastAnimationOutName = panelAnimations.animationOutName
    const [panelTask, skipPanelTask] = this.playWinningPanelAnimations(panelAnimations.animation)
    const [numTask, skipNumTask] = numberAnimation(0, payout, animationTime, winAmountNode)
    const skipWinningAnim = () => {
      skipNumTask()
      skipPanelTask()
    }
    this.playWinningPanelSound(odds, isTurbo)
    const clickCb = () => {
      handleSkip = true
      registerNormalSpaceBarEvent()
      this.winningPanel.onpointertap = null
      skipWinningAnim()
    }

    if (isTurbo) {
      skipPanelTask()
      await sleep(1500) // 原參考阿茲特克 await sleep(isSuperWin(odds) ? 2500 : 1500)
      this.winningPanel.onpointertap = clickCb
      registerSpaceBarEvent(clickCb)
    }
    else {
      this.winningPanel.onpointertap = clickCb
      registerSpaceBarEvent(clickCb)
    }

    await Promise.all([panelTask, numTask])

    this.playPanelStopSound(odds)

    await new Promise<void>((resolve) => {
      const closeWinningPanel = async () => {
        this.winningPanel.onpointertap = null
        const track = this.winningPanelSpine.state.setAnimation(0, lastAnimationOutName, false)
        await sleep(track.animation?.duration as number * 1000)
        this.winningPanel.visible = false
        this.winningPanel.removeChild(this.winningPanelSpine)
        this.winningPanel.removeChild(winAmountNode)
        this.winningPanelSpine.destroy()
        clearTimeout(closeTimeout)
        soundManager.playBGM()
        return resolve()
      }

      const autoCloseDuration = handleSkip ? 1000 : 2000

      const closeTimeout = setTimeout(() => {
        closeWinningPanel()
        return resolve()
      }, autoCloseDuration)
    })
  }

  private playWinningPanelSound(odds: number, isTurbo: boolean) {
    if (isSuperWin(odds)) {
      this.playSuperWinSound(isTurbo)
    }
    else if (isMegaWin(odds)) {
      this.playMegaWinSound(isTurbo)
    }
    else {
      this.playBigWinSound()
    }
  }

  private playBigWinSound() {
    soundManager.play(GameSoundType.Olympus_WinningPanel_bigwin)
  }

  private playMegaWinSound(isTurbo: boolean) {
    if (isTurbo) {
      soundManager.play(GameSoundType.Olympus_WinningPanel_megawin)
    }
    else {
      soundManager.play(GameSoundType.Olympus_WinningPanel_big2megawin)
    }
  }

  private playSuperWinSound(isTurbo: boolean) {
    if (isTurbo) {
      soundManager.play(GameSoundType.Olympus_WinningPanel_superwin)
    }
    else {
      soundManager.play(GameSoundType.Olympus_WinningPanel_big2superwin)
    }
  }

  private playPanelStopSound(odds: number) {
    soundManager.stop(GameSoundType.Olympus_WinningPanel_big2megawin)
    soundManager.stop(GameSoundType.Olympus_WinningPanel_big2superwin)
    soundManager.stop(GameSoundType.Olympus_WinningPanel_bigwin)
    soundManager.stop(GameSoundType.Olympus_WinningPanel_megawin)
    soundManager.stop(GameSoundType.Olympus_WinningPanel_superwin)
    if (isSuperWin(odds)) {
      soundManager.play(GameSoundType.Olympus_WinningPanel_superwinEnd)
    }
    else if (isMegaWin(odds)) {
      soundManager.play(GameSoundType.Olympus_WinningPanel_megawinEnd)
    }
    else {
      soundManager.play(GameSoundType.Olympus_WinningPanel_bigwinEnd)
    }
  }
}

export const winningManager = new WinningManager()
